import React from 'react'
import PieChartIcon from '@material-ui/icons/PieChartOutlined'
import ShowChartIcon from '@material-ui/icons/ShowChartOutlined'
import BubbleChartIcon from '@material-ui/icons/BubbleChartOutlined'
import DenaliLogo from '../../../../../apps/aiApp/Assistant/common/DenaliLogo'
import { useActiveInvestigation } from '../../ResearchAgentTray/hooks/useActiveInvestigation'
import { useResearchAgentEnabled } from '../../ResearchAgentTray/hooks/useResearchAgentEnabled'
import AllocationTool from './AllocationTool'
import ResearchTool from './ResearchTool'
import ScatterTool from './ScatterTool'
import TrendTool from './TrendTool'

// Pure data registry of utility-panel tools. Adding a new tool is:
//   1. Build a `<Tool payload={...} />` component (see ResearchTool.jsx as a
//      reference). It receives a single `payload` prop and uses
//      `useUtilityPanel()` to push header metadata + register toolbar
//      actions if needed.
//   2. Append an entry below.
//   3. Trigger it from anywhere via `openTool('<id>', payload?)`.
//
// Entry shape:
//   - id (string, unique) -- registry key + value passed to openTool()
//   - label (string) -- shown in the launcher menu
//   - renderIcon () => ReactNode -- a small icon (~16px) shown inside the
//     menu item's chip. Use whatever icon library is appropriate.
//   - component (React component) -- the body that renders inside the panel.
//   - iconBackground ('gradient' | 'none', default 'gradient') -- selects
//     how the menu item's icon chip is styled. 'gradient' = AI-themed
//     chip (research). 'none' = neutral / no chip background (allocation
//     and other generic tools). The chip is replaced by a check icon
//     when that tool is the one currently open in the panel.
//   - useAvailable () => boolean (optional) -- Hook called by
//     `UtilityMenuButton` to gate whether the user can reach this tool
//     at all (e.g. research depends on an entitlement). Omit when the
//     tool is always reachable. Hooks fire in registry iteration order;
//     adding / removing entries respects React's rules-of-hooks via the
//     "frozen + Object.values stable order" invariant -- only APPEND
//     new entries, never insert mid-list.
//   - useBusy () => boolean (optional) -- Hook called by
//     `UtilityMenuButton` to render a "still working" indicator in the
//     launcher (the small pulsing dot + the menu item's "In progress…"
//     secondary text). Same hook-order invariant as `useAvailable`.
//   - mode ('stack' | 'exclusive', default 'stack') -- how the tool
//     occupies the utility panel. 'stack' tools render inside a chart
//     tile alongside saved + transient charts in the unified
//     ChartStack. 'exclusive' tools take over the entire panel when
//     open (chart stack hidden); used for Research today because the
//     chat-style UI doesn't compose well into a small tile and its
//     workflow benefits from the full panel height.
//
// Keep entries alphabetical by id so the order is stable when consumers
// iterate (e.g. a future tool picker dropdown).
export const TOOL_REGISTRY = Object.freeze({
  allocation: {
    id: 'allocation',
    label: 'Allocation',
    renderIcon: () => <PieChartIcon style={{ fontSize: 16 }} />,
    iconBackground: 'none',
    mode: 'stack',
    component: AllocationTool
  },
  research: {
    id: 'research',
    label: 'Research',
    renderIcon: () => <DenaliLogo size='16px' flat />,
    iconBackground: 'gradient',
    mode: 'exclusive',
    component: ResearchTool,
    useAvailable: () => useResearchAgentEnabled().enabled,
    useBusy: () => useActiveInvestigation().isBusy
  },
  scatter: {
    id: 'scatter',
    label: 'Compare',
    renderIcon: () => <BubbleChartIcon style={{ fontSize: 16 }} />,
    iconBackground: 'none',
    mode: 'stack',
    component: ScatterTool
  },
  trend: {
    id: 'trend',
    label: 'Trend',
    renderIcon: () => <ShowChartIcon style={{ fontSize: 16 }} />,
    iconBackground: 'none',
    mode: 'stack',
    component: TrendTool
  }
})
